import { useQuery } from '@apollo/client'
import { Chip, Divider, Typography } from '@mui/material'
import { candidateRouteConfig } from 'candidate/routes'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as EmptyProfile } from 'shared/assets/new_empty_profile.svg'
import DashboardCard from 'shared/components/DashboardCard'
import { getPxAsRem } from 'shared/utils'
import {
    DashboardCardRow,
    DashboardCardRowTitle,
    DashboardCardSkeleton,
    EmptyStateContainer,
} from './styled'
import {
    ProfileMeQueryDocument,
    ProfileMeQueryQuery,
} from '__generated__/graphql'

const ProfileCard = () => {
    const [profileMeData, setCandidateMeData] = useState<ProfileMeQueryQuery>()
    const [resume, setResume] = useState<string>()

    const { loading } = useQuery(ProfileMeQueryDocument, {
        fetchPolicy: 'network-only',
        onCompleted(data) {
            const queryData = data
            const resumeDoc = queryData.candidateMe.documents?.find(
                (doc) => doc.documentType === 'Resume'
            )
            setCandidateMeData(queryData)
            if (resumeDoc) {
                const resumeFilename = resumeDoc.documentKey?.split('/').pop()
                setResume(resumeFilename)
            }
        },
    })

    const navigate = useNavigate()

    const isProfileEmpty = (profileMeData: ProfileMeQueryQuery) => {
        if (
            profileMeData.candidateMe.documents?.length === 0 &&
            profileMeData.candidateMe.workExperience?.length === 0 &&
            !profileMeData.candidateMe.linkedinUrl &&
            profileMeData.candidateMe.references?.length === 0 &&
            profileMeData.candidateMe.visaStatuses?.length === 0
        ) {
            return true
        }
        return false
    }

    const handleClick = () => {
        navigate(candidateRouteConfig.profile.path)
    }

    if (loading)
        return (
            <DashboardCard title="Profile">
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
                <Divider />
                <DashboardCardSkeleton />
            </DashboardCard>
        )

    if (!profileMeData || isProfileEmpty(profileMeData)) {
        return (
            <DashboardCard title="Profile" onClick={handleClick}>
                <EmptyStateContainer>
                    <EmptyProfile />
                    <Typography variant="body1">
                        You currently have no filled out profile information.
                    </Typography>
                </EmptyStateContainer>
            </DashboardCard>
        )
    }

    return (
        <DashboardCard title="Profile" onClick={handleClick}>
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Resume
                </DashboardCardRowTitle>
                <Typography variant="body1">{resume || '-'}</Typography>
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Personal Phone
                </DashboardCardRowTitle>

                <Typography noWrap variant="body1">
                    {profileMeData.candidateMe.user?.phone}
                </Typography>
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    LinkedIn Account
                </DashboardCardRowTitle>

                <Typography noWrap variant="body1">
                    {profileMeData.candidateMe.linkedinUrl || '-'}
                </Typography>
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    References Information
                </DashboardCardRowTitle>
                <Typography noWrap variant="body1">
                    {profileMeData.candidateMe.references?.[0]
                        ? `${profileMeData.candidateMe.references?.[0]?.firstName} ${profileMeData.candidateMe.references?.[0]?.lastName}`
                        : '-'}
                </Typography>
                {profileMeData.candidateMe.references &&
                    profileMeData.candidateMe.references?.length > 1 && (
                        <Chip
                            size="small"
                            sx={{ marginLeft: getPxAsRem(4) }}
                            label={`+${profileMeData.candidateMe.references.length - 1}`}
                            title={`${profileMeData.candidateMe.references.length - 1} additional reference(s)`}
                        />
                    )}
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow>
                <DashboardCardRowTitle variant="body1">
                    Staffer Name
                </DashboardCardRowTitle>
                <Typography noWrap variant="body1">
                    {profileMeData.candidateMe.staffers &&
                    profileMeData.candidateMe.staffers?.length !== 0
                        ? `${profileMeData.candidateMe.staffers[0].staffer?.firstName} ${profileMeData.candidateMe.staffers[0].staffer?.lastName}`
                        : '-'}
                </Typography>
            </DashboardCardRow>
            <Divider />
            <DashboardCardRow sx={{ paddingBottom: '0 !important' }}>
                <DashboardCardRowTitle variant="body1">
                    Visa Status
                </DashboardCardRowTitle>
                <Typography noWrap variant="body1">
                    {profileMeData.candidateMe.visaStatuses?.[0]
                        ? profileMeData.candidateMe.visaStatuses?.[0]
                              ?.visaStatus?.value
                        : '-'}
                </Typography>
                {profileMeData.candidateMe.visaStatuses &&
                    profileMeData.candidateMe.visaStatuses?.length > 1 && (
                        <Chip
                            size="small"
                            sx={{ marginLeft: getPxAsRem(4) }}
                            label={`+${profileMeData.candidateMe.visaStatuses.length - 1}`}
                            title={`${profileMeData.candidateMe.visaStatuses.length - 1} additional visa(s)`}
                        />
                    )}
            </DashboardCardRow>
        </DashboardCard>
    )
}
export default ProfileCard
